import { assert } from '@ember/debug';
import { typeOf } from '@ember/utils';
import { readOnly } from '@ember/object/computed';
import Service, { inject as service } from '@ember/service';
import { ActionItemTypes } from '@dashboard/shared-constants/setup-guide/action-types';
import { MilestonesVariantTypes } from '@square/dashboard-data/models/squareup/setup-guide/config/milestones';
const DEFAULT_BUTTON_TRANSLATION_KEY = 'button.continue';
const DEFAULT_BUTTON_NEXT_TRANSLATION_KEY = 'button.next';
const DEFAULT_STEP_COMPLETE_TRANSLATION_KEY = 'setupGuide.stepComplete';
const DEFAULT_SECONDARY_BUTTON_TRANSLATION_KEYS = {
  [MilestonesVariantTypes.DEFAULT]: 'setupGuide.skip.pos',
  [MilestonesVariantTypes.RETAIL]: 'setupGuide.skip.retail'
};

// Any key that is defined as an object needs to have a MilestonesVariantTypes.DEFAULT key defined as a fallback.
const titleTranslationKeys = {
  [ActionItemTypes.ACTIVATE_TWO_FACTOR_AUTH]: 'setupGuide.actionItems.activateTwoFactorAuth.title',
  [ActionItemTypes.ADD_A_LOCATION]: {
    [MilestonesVariantTypes.DEFAULT]: 'setupGuide.actionItems.addALocation.title.pos',
    [MilestonesVariantTypes.RETAIL]: 'setupGuide.actionItems.addALocation.title.retail'
  },
  [ActionItemTypes.BUY_HARDWARE]: {
    [MilestonesVariantTypes.DEFAULT]: 'setupGuide.actionItems.buyHardware.title.pos',
    [MilestonesVariantTypes.RETAIL]: 'setupGuide.actionItems.buyHardware.title.retail'
  },
  [ActionItemTypes.BUY_POS_HARDWARE]: 'setupGuide.actionItems.buyPosHardware.title',
  [ActionItemTypes.CONNECT_OTHER_SERVICES]: 'setupGuide.actionItems.connectOtherServices.title',
  [ActionItemTypes.CONNECT_WEEBLY]: {
    [MilestonesVariantTypes.DEFAULT]: 'setupGuide.actionItems.connectWeebly.title.pos',
    [MilestonesVariantTypes.RETAIL]: 'setupGuide.actionItems.connectWeebly.title.retail'
  },
  [ActionItemTypes.CREATE_BAR_CODE_LABEL]: 'setupGuide.actionItems.createBarCodeLabel.title',
  [ActionItemTypes.CUSTOMIZE_RECEIPTS]: {
    [MilestonesVariantTypes.DEFAULT]: 'setupGuide.actionItems.customizeReceipts.title.pos',
    [MilestonesVariantTypes.RETAIL]: 'setupGuide.actionItems.customizeReceipts.title.retail'
  },
  [ActionItemTypes.CUSTOMIZE_INVOICE_RECEIPTS]: 'setupGuide.actionItems.customizeInvoiceReceipts.title',
  [ActionItemTypes.DOWNLOAD_THE_APP]: 'setupGuide.actionItems.downloadTheApp.title',
  [ActionItemTypes.DOWNLOAD_THE_INVOICES_APP]: 'setupGuide.actionItems.downloadTheInvoicesApp.title',
  [ActionItemTypes.DOWNLOAD_THE_RETAIL_APP]: 'setupGuide.actionItems.downloadTheRetailApp.title',
  [ActionItemTypes.DOWNLOAD_THE_RST_APP]: 'setupGuide.actionItems.downloadTheRestaurantsApp.title',
  [ActionItemTypes.ENTER_DIRECTOR_INFO]: 'setupGuide.actionItems.enterDirectorInfo.title',
  [ActionItemTypes.ENTER_UK_DIRECTOR_INFO]: 'setupGuide.actionItems.enterUkDirectorInfo.title',
  [ActionItemTypes.FINISH_ACTIVATING]: {
    [MilestonesVariantTypes.DEFAULT]: 'setupGuide.actionItems.finishActivating.title.pos',
    [MilestonesVariantTypes.RETAIL]: 'setupGuide.actionItems.finishActivating.title.retail'
  },
  [ActionItemTypes.FINISH_ACTIVATING_PROVISIONAL]: 'setupGuide.actionItems.finishActivating.title.pos',
  [ActionItemTypes.SUBMIT_DOC_IDV]: 'setupGuide.actionItems.finishActivating.title.pos',
  [ActionItemTypes.FINISH_OWNER_DIRECTOR_IDV]: 'setupGuide.actionItems.finishOwnerDirectorIdv.title.pos',
  [ActionItemTypes.IMPORT_INVENTORY]: 'setupGuide.actionItems.importInventory.title',
  [ActionItemTypes.LINK_ECOM_SITE]: 'setupGuide.actionItems.linkEcomSite.title',
  [ActionItemTypes.LINK_YOUR_BANK_ACCOUNT]: {
    [MilestonesVariantTypes.DEFAULT]: 'setupGuide.actionItems.linkYourBankAccount.title.pos',
    [MilestonesVariantTypes.RETAIL]: 'setupGuide.actionItems.linkYourBankAccount.title.retail'
  },
  [ActionItemTypes.MANAGE_EMPLOYEES]: {
    [MilestonesVariantTypes.DEFAULT]: 'setupGuide.actionItems.manageEmployees.title.pos',
    [MilestonesVariantTypes.RETAIL]: 'setupGuide.actionItems.manageEmployees.title.retail'
  },
  [ActionItemTypes.SAVED_A_MENU_ITEM]: 'setupGuide.actionItems.savedAMenuItem.title',
  [ActionItemTypes.SELL_WITH_SQUARE_FOR_RETAIL]: 'setupGuide.actionItems.sellWithSquareForRetail.title',
  [ActionItemTypes.SEND_AN_INVOICE]: 'setupGuide.actionItems.sendAnInvoice.title',
  [ActionItemTypes.SET_CLOSE_OF_DAY]: {
    [MilestonesVariantTypes.DEFAULT]: 'setupGuide.actionItems.setCloseOfDay.title.pos',
    [MilestonesVariantTypes.RETAIL]: 'setupGuide.actionItems.setCloseOfDay.title.retail'
  },
  [ActionItemTypes.SET_UP_DEVICES]: 'setupGuide.actionItems.setUpDevices.title',
  [ActionItemTypes.SET_UP_DISCOUNTS]: {
    [MilestonesVariantTypes.DEFAULT]: 'setupGuide.actionItems.setUpDiscounts.title.pos',
    [MilestonesVariantTypes.RETAIL]: 'setupGuide.actionItems.setUpDiscounts.title.retail'
  },
  [ActionItemTypes.SET_UP_ECOMMERCE_STORE]: 'setupGuide.actionItems.ecommercePlus.title',
  [ActionItemTypes.SET_UP_FLOOR_PLAN]: 'setupGuide.actionItems.setUpFloorPlan.title',
  [ActionItemTypes.SET_UP_ITEMS]: {
    [MilestonesVariantTypes.DEFAULT]: 'setupGuide.actionItems.setUpItems.title.pos',
    [MilestonesVariantTypes.RETAIL]: 'setupGuide.actionItems.setUpItems.title.retail'
  },
  [ActionItemTypes.SET_UP_RETAIL_DEVICES]: 'setupGuide.actionItems.setUpRetailDevices.title',
  // TODO(RST-5601) Delete SET_UP_RST_GRID_LAYOUT once removed from Checklist
  [ActionItemTypes.SET_UP_RST_GRID_LAYOUT]: 'setupGuide.actionItems.setUpRstGridLayout.title',
  [ActionItemTypes.SET_UP_TAXES]: {
    [MilestonesVariantTypes.DEFAULT]: 'setupGuide.actionItems.setUpTaxes.title.pos',
    [MilestonesVariantTypes.RETAIL]: 'setupGuide.actionItems.setUpTaxes.title.retail'
  },
  [ActionItemTypes.SET_UP_VENDORS]: {
    [MilestonesVariantTypes.DEFAULT]: 'setupGuide.actionItems.setUpVendors.title.pos',
    [MilestonesVariantTypes.RETAIL]: 'setupGuide.actionItems.setUpVendors.title.retail'
  },
  [ActionItemTypes.SUBSCRIBE_TO_RETAIL]: 'setupGuide.actionItems.subscribeToRetail.title',
  [ActionItemTypes.TAKE_FIRST_PAYMENT]: 'setupGuide.actionItems.takeFirstPayment.title',
  [ActionItemTypes.UPDATE_BUSINESS_DETAILS]: 'setupGuide.actionItems.updateBusinessDetails.title',
  [ActionItemTypes.VERIFY_EMAIL]: 'setupGuide.actionItems.verifyEmail.title',
  [ActionItemTypes.VERIFY_PAYMENTS_AND_TRANSFERS]: 'setupGuide.actionItems.verifyPaymentsAndTransfers.title',
  [ActionItemTypes.VIEW_INVOICE_SETTINGS]: 'setupGuide.actionItems.viewInvoiceSettings.title',
  [ActionItemTypes.VIEW_SALES_REPORTS]: 'setupGuide.actionItems.viewSalesReports.title',
  [ActionItemTypes.VIEW_SOFTWARE_OVERVIEW]: 'setupGuide.actionItems.viewSoftwareOverview.title',
  [ActionItemTypes.ENABLE_PICKUP_AND_DELIVERY]: 'setupGuide.actionItems.enablePickupAndDelivery.title',
  [ActionItemTypes.SET_UP_KDS]: 'setupGuide.actionItems.setUpKDS.title',
  [ActionItemTypes.DOWNLOAD_THE_KDS_APP]: 'setupGuide.actionItems.downloadTheKDSApp.title',
  [ActionItemTypes.VIEW_KITCHEN_REPORTS]: 'setupGuide.actionItems.viewKitchenReports.title',
  [ActionItemTypes.RETAIL_WATCH_TOUR]: 'setupGuide.actionItems.retailWatchTour.title',
  [ActionItemTypes.RST_WATCH_TOUR]: 'setupGuide.actionItems.rstWatchTour.title',
  [ActionItemTypes.SET_UP_ONLINE_CHECKOUT]: 'setupGuide.actionItems.setupOnlineCheckout.title',
  [ActionItemTypes.SET_UP_BUTTON_SETTINGS]: 'setupGuide.actionItems.setupButtonSettings.title',
  [ActionItemTypes.SET_UP_FULFILLMENT_SETTINGS]: 'setupGuide.actionItems.setupFulfillmentSettings.title',
  [ActionItemTypes.SIGN_UP_APPOINTMENTS]: 'setupGuide.actionItems.signUpAppointments.title',
  [ActionItemTypes.ADD_BILLING_INSTRUMENT]: 'setupGuide.title',
  [ActionItemTypes.PURCHASE_PRICING_PLAN_HARDWARE]: 'setupGuide.title'
};
const modalTitleTranslationKeys = {
  [ActionItemTypes.ENABLE_PICKUP_AND_DELIVERY]: 'setupGuide.actionItems.enablePickupAndDelivery.modalTitle',
  [ActionItemTypes.SET_UP_KDS]: 'setupGuide.actionItems.setUpKDS.modalTitle',
  [ActionItemTypes.VIEW_KITCHEN_REPORTS]: 'setupGuide.actionItems.viewKitchenReports.modalTitle',
  [ActionItemTypes.DOWNLOAD_THE_KDS_APP]: 'setupGuide.actionItems.downloadTheKDSApp.modalTitle',
  [ActionItemTypes.SAVED_A_MENU_ITEM]: 'setupGuide.actionItems.savedAMenuItem.modalTitle',
  [ActionItemTypes.SET_UP_ECOMMERCE_STORE]: 'setupGuide.actionItems.ecommercePlus.modalTitle',
  [ActionItemTypes.SET_UP_ONLINE_CHECKOUT]: 'setupGuide.actionItems.setupOnlineCheckout.modalTitle',
  [ActionItemTypes.SET_UP_BUTTON_SETTINGS]: 'setupGuide.actionItems.setupButtonSettings.modalTitle',
  [ActionItemTypes.SET_UP_FULFILLMENT_SETTINGS]: 'setupGuide.actionItems.setupFulfillmentSettings.modalTitle'
};
const introductionTranslationKeys = {
  [ActionItemTypes.ACTIVATE_TWO_FACTOR_AUTH]: 'setupGuide.actionItems.activateTwoFactorAuth.introduction',
  [ActionItemTypes.ADD_A_LOCATION]: {
    [MilestonesVariantTypes.DEFAULT]: 'setupGuide.actionItems.addALocation.introduction.pos',
    [MilestonesVariantTypes.MIXED]: 'setupGuide.actionItems.addALocation.introduction.pos',
    [MilestonesVariantTypes.REGISTER_POS]: 'setupGuide.actionItems.addALocation.introduction.pos',
    [MilestonesVariantTypes.RETAIL]: 'setupGuide.actionItems.addALocation.introduction.retail',
    [MilestonesVariantTypes.RST]: 'setupGuide.actionItems.addALocation.introduction.pos',
    [MilestonesVariantTypes.RST_FREE]: 'setupGuide.actionItems.addALocation.introduction.pos',
    [MilestonesVariantTypes.TERMINAL]: 'setupGuide.actionItems.addALocation.introduction.pos'
  },
  [ActionItemTypes.BUY_HARDWARE]: {
    [MilestonesVariantTypes.TERMINAL]: 'setupGuide.actionItems.buyHardware.introduction.terminal',
    [MilestonesVariantTypes.REGISTER_POS]: 'setupGuide.actionItems.buyHardware.introduction.pos',
    [MilestonesVariantTypes.RETAIL]: 'setupGuide.actionItems.buyHardware.introduction.retail',
    [MilestonesVariantTypes.RST]: 'setupGuide.actionItems.buyHardware.introduction.restaurants',
    [MilestonesVariantTypes.RST_FREE]: 'setupGuide.actionItems.buyHardware.introduction.restaurants',
    [MilestonesVariantTypes.DEFAULT]: 'setupGuide.actionItems.buyHardware.introduction.terminal'
  },
  [ActionItemTypes.BUY_POS_HARDWARE]: 'setupGuide.actionItems.buyPosHardware.introduction',
  [ActionItemTypes.CONNECT_OTHER_SERVICES]: 'setupGuide.actionItems.connectOtherServices.introduction.pos',
  [ActionItemTypes.CONNECT_WEEBLY]: {
    [MilestonesVariantTypes.DEFAULT]: 'setupGuide.actionItems.connectWeebly.introduction.pos',
    [MilestonesVariantTypes.RETAIL]: 'setupGuide.actionItems.connectWeebly.introduction.retail'
  },
  [ActionItemTypes.CREATE_BAR_CODE_LABEL]: 'setupGuide.actionItems.createBarCodeLabel.introduction',
  [ActionItemTypes.CUSTOMIZE_RECEIPTS]: 'setupGuide.actionItems.customizeReceipts.introduction',
  [ActionItemTypes.CUSTOMIZE_INVOICE_RECEIPTS]: 'setupGuide.actionItems.customizeInvoiceReceipts.introduction',
  [ActionItemTypes.DOWNLOAD_THE_APP]: 'setupGuide.actionItems.downloadTheApp.introduction',
  [ActionItemTypes.DOWNLOAD_THE_INVOICES_APP]: 'setupGuide.actionItems.downloadTheInvoicesApp.introduction',
  [ActionItemTypes.DOWNLOAD_THE_RETAIL_APP]: 'setupGuide.actionItems.downloadTheRetailApp.introduction',
  [ActionItemTypes.DOWNLOAD_THE_RST_APP]: 'setupGuide.actionItems.downloadTheRestaurantsApp.introduction',
  [ActionItemTypes.ENTER_DIRECTOR_INFO]: 'setupGuide.actionItems.enterDirectorInfo.introduction',
  [ActionItemTypes.ENTER_UK_DIRECTOR_INFO]: 'setupGuide.actionItems.enterUkDirectorInfo.introduction',
  [ActionItemTypes.FINISH_ACTIVATING]: {
    [MilestonesVariantTypes.DEFAULT]: 'setupGuide.actionItems.finishActivating.introduction.pos',
    [MilestonesVariantTypes.RETAIL]: 'setupGuide.actionItems.finishActivating.introduction.retail'
  },
  [ActionItemTypes.FINISH_ACTIVATING_PROVISIONAL]: 'setupGuide.actionItems.finishActivatingProvisional.introduction',
  [ActionItemTypes.SUBMIT_DOC_IDV]: 'setupGuide.actionItems.finishActivating.introduction.pos',
  [ActionItemTypes.FINISH_OWNER_DIRECTOR_IDV]: 'setupGuide.actionItems.finishOwnerDirectorIdv.introduction.pos',
  [ActionItemTypes.IMPORT_INVENTORY]: 'setupGuide.actionItems.importInventory.introduction',
  [ActionItemTypes.LINK_ECOM_SITE]: 'setupGuide.actionItems.linkEcomSite.introduction',
  [ActionItemTypes.LINK_YOUR_BANK_ACCOUNT]: 'setupGuide.actionItems.linkYourBankAccount.introduction',
  [ActionItemTypes.MANAGE_EMPLOYEES]: 'setupGuide.actionItems.manageEmployees.introduction.pos',
  [ActionItemTypes.SAVED_A_MENU_ITEM]: 'setupGuide.actionItems.savedAMenuItem.introduction',
  [ActionItemTypes.SELL_WITH_SQUARE_FOR_RETAIL]: 'setupGuide.actionItems.sellWithSquareForRetail.introduction',
  [ActionItemTypes.SEND_AN_INVOICE]: 'setupGuide.actionItems.sendAnInvoice.introduction',
  [ActionItemTypes.SET_CLOSE_OF_DAY]: 'setupGuide.actionItems.setCloseOfDay.introduction',
  [ActionItemTypes.SET_UP_KDS]: 'setupGuide.actionItems.setUpKDS.introduction',
  [ActionItemTypes.DOWNLOAD_THE_KDS_APP]: 'setupGuide.actionItems.downloadTheKDSApp.introduction',
  [ActionItemTypes.VIEW_KITCHEN_REPORTS]: 'setupGuide.actionItems.viewKitchenReports.introduction',
  [ActionItemTypes.SET_UP_DEVICES]: 'setupGuide.actionItems.setUpDevices.introduction',
  [ActionItemTypes.SET_UP_DISCOUNTS]: 'setupGuide.actionItems.setUpDiscounts.introduction',
  [ActionItemTypes.SET_UP_ECOMMERCE_STORE]: 'setupGuide.actionItems.ecommercePlus.introduction',
  [ActionItemTypes.SET_UP_FLOOR_PLAN]: 'setupGuide.actionItems.setUpFloorPlan.introduction',
  [ActionItemTypes.SET_UP_ITEMS]: {
    [MilestonesVariantTypes.DEFAULT]: 'setupGuide.actionItems.setUpItems.introduction.pos',
    [MilestonesVariantTypes.RETAIL]: 'setupGuide.actionItems.setUpItems.introduction.retail'
  },
  [ActionItemTypes.SET_UP_RETAIL_DEVICES]: 'setupGuide.actionItems.setUpRetailDevices.introduction',
  // TODO(RST-5601) Delete SET_UP_RST_GRID_LAYOUT once removed from Checklist
  [ActionItemTypes.SET_UP_RST_GRID_LAYOUT]: 'setupGuide.actionItems.setUpRstGridLayout.introduction',
  [ActionItemTypes.SET_UP_TAXES]: {
    [MilestonesVariantTypes.DEFAULT]: 'setupGuide.actionItems.setUpTaxes.introduction.pos',
    [MilestonesVariantTypes.TERMINAL]: 'setupGuide.actionItems.setUpTaxes.introduction.terminal',
    [MilestonesVariantTypes.RETAIL]: 'setupGuide.actionItems.setUpTaxes.introduction.retail',
    [MilestonesVariantTypes.RST]: 'setupGuide.actionItems.setUpTaxes.introduction.restaurants',
    [MilestonesVariantTypes.RST_FREE]: 'setupGuide.actionItems.setUpTaxes.introduction.restaurants'
  },
  [ActionItemTypes.SET_UP_VENDORS]: 'setupGuide.actionItems.setUpVendors.introduction',
  [ActionItemTypes.SUBSCRIBE_TO_RETAIL]: 'setupGuide.actionItems.subscribeToRetail.introduction',
  [ActionItemTypes.TAKE_FIRST_PAYMENT]: 'setupGuide.actionItems.takeFirstPayment.introduction',
  [ActionItemTypes.UPDATE_BUSINESS_DETAILS]: 'setupGuide.actionItems.updateBusinessDetails.introduction',
  [ActionItemTypes.VERIFY_EMAIL]: 'setupGuide.actionItems.verifyEmail.introduction',
  [ActionItemTypes.VERIFY_PAYMENTS_AND_TRANSFERS]: 'setupGuide.actionItems.verifyPaymentsAndTransfers.introduction',
  [ActionItemTypes.VIEW_INVOICE_SETTINGS]: 'setupGuide.actionItems.viewInvoiceSettings.introduction',
  [ActionItemTypes.VIEW_SALES_REPORTS]: 'setupGuide.actionItems.viewSalesReports.introduction',
  [ActionItemTypes.VIEW_SOFTWARE_OVERVIEW]: 'setupGuide.actionItems.viewSoftwareOverview.introduction',
  [ActionItemTypes.ENABLE_PICKUP_AND_DELIVERY]: 'setupGuide.actionItems.enablePickupAndDelivery.introduction',
  [ActionItemTypes.RETAIL_WATCH_TOUR]: 'setupGuide.actionItems.retailWatchTour.introduction',
  [ActionItemTypes.RST_WATCH_TOUR]: 'setupGuide.actionItems.rstWatchTour.introduction',
  [ActionItemTypes.SET_UP_ONLINE_CHECKOUT]: 'setupGuide.actionItems.setupOnlineCheckout.introduction',
  [ActionItemTypes.SET_UP_BUTTON_SETTINGS]: 'setupGuide.actionItems.setupButtonSettings.introduction',
  [ActionItemTypes.SET_UP_FULFILLMENT_SETTINGS]: 'setupGuide.actionItems.setupFulfillmentSettings.introduction',
  [ActionItemTypes.SIGN_UP_APPOINTMENTS]: 'setupGuide.actionItems.signUpAppointments.introduction',
  [ActionItemTypes.ADD_BILLING_INSTRUMENT]: 'setupGuide.title',
  [ActionItemTypes.PURCHASE_PRICING_PLAN_HARDWARE]: 'setupGuide.title',
  [ActionItemTypes.FINISH_BENEFICIAL_OWNER_IDV_US]: 'setupGuide.actionItems.finishBeneficialOwnerIdvUs.title.pos'
};
const descriptionTranslationKeys = {
  [ActionItemTypes.ACTIVATE_TWO_FACTOR_AUTH]: 'setupGuide.actionItems.activateTwoFactorAuth.description',
  [ActionItemTypes.ADD_A_LOCATION]: 'setupGuide.actionItems.addALocation.description',
  [ActionItemTypes.BUY_HARDWARE]: {
    [MilestonesVariantTypes.DEFAULT]: 'setupGuide.actionItems.buyHardware.description.pos',
    [MilestonesVariantTypes.RETAIL]: 'setupGuide.actionItems.buyHardware.description.retail',
    [MilestonesVariantTypes.RST]: 'setupGuide.actionItems.buyHardware.description.restaurants',
    [MilestonesVariantTypes.RST_FREE]: 'setupGuide.actionItems.buyHardware.description.restaurants'
  },
  [ActionItemTypes.BUY_POS_HARDWARE]: 'setupGuide.actionItems.buyPosHardware.description',
  [ActionItemTypes.CONNECT_OTHER_SERVICES]: 'setupGuide.actionItems.connectOtherServices.description',
  [ActionItemTypes.CONNECT_WEEBLY]: 'setupGuide.actionItems.connectWeebly.description',
  [ActionItemTypes.CREATE_BAR_CODE_LABEL]: 'setupGuide.actionItems.createBarCodeLabel.description',
  [ActionItemTypes.CUSTOMIZE_RECEIPTS]: {
    [MilestonesVariantTypes.DEFAULT]: 'setupGuide.actionItems.customizeReceipts.description.pos',
    [MilestonesVariantTypes.RST]: 'setupGuide.actionItems.customizeReceipts.description.restaurants',
    [MilestonesVariantTypes.RST_FREE]: 'setupGuide.actionItems.customizeReceipts.description.restaurants'
  },
  [ActionItemTypes.CUSTOMIZE_INVOICE_RECEIPTS]: 'setupGuide.actionItems.customizeInvoiceReceipts.description',
  [ActionItemTypes.DOWNLOAD_THE_APP]: 'setupGuide.actionItems.downloadTheApp.description',
  [ActionItemTypes.DOWNLOAD_THE_INVOICES_APP]: 'setupGuide.actionItems.downloadTheInvoicesApp.description',
  [ActionItemTypes.DOWNLOAD_THE_RETAIL_APP]: 'setupGuide.actionItems.downloadTheRetailApp.description',
  [ActionItemTypes.DOWNLOAD_THE_RST_APP]: 'setupGuide.actionItems.downloadTheRestaurantsApp.description',
  [ActionItemTypes.ENTER_DIRECTOR_INFO]: 'setupGuide.actionItems.enterDirectorInfo.description',
  [ActionItemTypes.ENTER_UK_DIRECTOR_INFO]: 'setupGuide.actionItems.enterUkDirectorInfo.description',
  [ActionItemTypes.FINISH_ACTIVATING]: 'setupGuide.actionItems.finishActivating.description',
  [ActionItemTypes.SUBMIT_DOC_IDV]: 'setupGuide.actionItems.submitDocIdv.description',
  [ActionItemTypes.FINISH_OWNER_DIRECTOR_IDV]: 'setupGuide.actionItems.finishOwnerDirectorIdv.description.pos',
  [ActionItemTypes.IMPORT_INVENTORY]: 'setupGuide.actionItems.importInventory.description',
  [ActionItemTypes.LINK_YOUR_BANK_ACCOUNT]: 'setupGuide.actionItems.linkYourBankAccount.description',
  [ActionItemTypes.MANAGE_EMPLOYEES]: 'setupGuide.actionItems.manageEmployees.description',
  [ActionItemTypes.SAVED_A_MENU_ITEM]: 'setupGuide.actionItems.savedAMenuItem.description',
  [ActionItemTypes.SEND_AN_INVOICE]: 'setupGuide.actionItems.sendAnInvoice.description',
  [ActionItemTypes.SET_CLOSE_OF_DAY]: 'setupGuide.actionItems.setCloseOfDay.description',
  [ActionItemTypes.SET_UP_DEVICES]: 'setupGuide.actionItems.setUpDevices.description',
  [ActionItemTypes.SET_UP_DISCOUNTS]: 'setupGuide.actionItems.setUpDiscounts.description',
  [ActionItemTypes.SET_UP_ECOMMERCE_STORE]: 'setupGuide.actionItems.ecommercePlus.description',
  [ActionItemTypes.SET_UP_FLOOR_PLAN]: 'setupGuide.actionItems.setUpFloorPlan.description',
  [ActionItemTypes.SET_UP_ITEMS]: 'setupGuide.actionItems.setUpItems.description',
  [ActionItemTypes.SET_UP_RETAIL_DEVICES]: 'setupGuide.actionItems.setUpRetailDevices.description',
  // TODO(RST-5601) Delete SET_UP_RST_GRID_LAYOUT once removed from Checklist
  [ActionItemTypes.SET_UP_RST_GRID_LAYOUT]: 'setupGuide.actionItems.setUpRstGridLayout.description',
  [ActionItemTypes.SET_UP_TAXES]: {
    [MilestonesVariantTypes.DEFAULT]: 'setupGuide.actionItems.setUpTaxes.description.pos',
    [MilestonesVariantTypes.TERMINAL]: 'setupGuide.actionItems.setUpTaxes.description.terminal',
    [MilestonesVariantTypes.RST]: 'setupGuide.actionItems.setUpTaxes.description.restaurants',
    [MilestonesVariantTypes.RST_FREE]: 'setupGuide.actionItems.setUpTaxes.description.restaurants'
  },
  [ActionItemTypes.SET_UP_VENDORS]: 'setupGuide.actionItems.setUpVendors.description',
  [ActionItemTypes.SUBSCRIBE_TO_RETAIL]: 'setupGuide.actionItems.subscribeToRetail.description',
  [ActionItemTypes.TAKE_FIRST_PAYMENT]: 'setupGuide.actionItems.takeFirstPayment.description',
  [ActionItemTypes.UPDATE_BUSINESS_DETAILS]: 'setupGuide.actionItems.updateBusinessDetails.description',
  [ActionItemTypes.VERIFY_EMAIL]: 'setupGuide.actionItems.verifyEmail.description',
  [ActionItemTypes.VERIFY_PAYMENTS_AND_TRANSFERS]: 'setupGuide.actionItems.verifyPaymentsAndTransfers.description',
  [ActionItemTypes.VIEW_INVOICE_SETTINGS]: 'setupGuide.actionItems.viewInvoiceSettings.description',
  [ActionItemTypes.VIEW_SALES_REPORTS]: 'setupGuide.actionItems.viewSalesReports.description',
  [ActionItemTypes.VIEW_SOFTWARE_OVERVIEW]: 'setupGuide.actionItems.viewSoftwareOverview.description',
  [ActionItemTypes.ENABLE_PICKUP_AND_DELIVERY]: 'setupGuide.actionItems.enablePickupAndDelivery.description',
  [ActionItemTypes.SET_UP_KDS]: 'setupGuide.actionItems.setUpKDS.description',
  [ActionItemTypes.DOWNLOAD_THE_KDS_APP]: 'setupGuide.actionItems.downloadTheKDSApp.description',
  [ActionItemTypes.VIEW_KITCHEN_REPORTS]: 'setupGuide.actionItems.viewKitchenReports.description',
  [ActionItemTypes.RST_WATCH_TOUR]: 'setupGuide.actionItems.rstWatchTour.introduction',
  [ActionItemTypes.SET_UP_ONLINE_CHECKOUT]: 'setupGuide.actionItems.setupOnlineCheckout.description',
  [ActionItemTypes.SET_UP_BUTTON_SETTINGS]: 'setupGuide.actionItems.setupButtonSettings.description',
  [ActionItemTypes.SET_UP_FULFILLMENT_SETTINGS]: 'setupGuide.actionItems.setupFulfillmentSettings.description',
  [ActionItemTypes.SIGN_UP_APPOINTMENTS]: 'setupGuide.actionItems.signUpAppointments.description',
  [ActionItemTypes.ADD_BILLING_INSTRUMENT]: 'setupGuide.title',
  [ActionItemTypes.PURCHASE_PRICING_PLAN_HARDWARE]: 'setupGuide.title'
};
const completedTranslationKeys = {
  [ActionItemTypes.ACTIVATE_TWO_FACTOR_AUTH]: 'setupGuide.actionItems.activateTwoFactorAuth.completed',
  [ActionItemTypes.ADD_A_LOCATION]: {
    [MilestonesVariantTypes.DEFAULT]: 'setupGuide.actionItems.addALocation.completed.pos',
    [MilestonesVariantTypes.RETAIL]: 'setupGuide.actionItems.addALocation.completed.retail'
  },
  [ActionItemTypes.BUY_HARDWARE]: {
    [MilestonesVariantTypes.DEFAULT]: 'setupGuide.actionItems.buyHardware.completed.pos',
    [MilestonesVariantTypes.RST]: 'setupGuide.actionItems.buyHardware.completed.restaurants',
    [MilestonesVariantTypes.RST_FREE]: 'setupGuide.actionItems.buyHardware.completed.restaurants'
  },
  [ActionItemTypes.BUY_POS_HARDWARE]: 'setupGuide.actionItems.buyPosHardware.completed',
  [ActionItemTypes.CONNECT_OTHER_SERVICES]: 'setupGuide.actionItems.connectOtherServices.completed',
  [ActionItemTypes.CONNECT_WEEBLY]: 'setupGuide.actionItems.connectWeebly.completed',
  [ActionItemTypes.CREATE_BAR_CODE_LABEL]: 'setupGuide.actionItems.createBarCodeLabel.completed',
  [ActionItemTypes.CUSTOMIZE_RECEIPTS]: {
    [MilestonesVariantTypes.DEFAULT]: 'setupGuide.actionItems.customizeReceipts.completed.pos',
    [MilestonesVariantTypes.RST]: 'setupGuide.actionItems.customizeReceipts.completed.restaurants',
    [MilestonesVariantTypes.RST_FREE]: 'setupGuide.actionItems.customizeReceipts.completed.restaurants'
  },
  [ActionItemTypes.CUSTOMIZE_INVOICE_RECEIPTS]: 'setupGuide.actionItems.customizeInvoiceReceipts.completed',
  [ActionItemTypes.DOWNLOAD_THE_APP]: 'setupGuide.actionItems.downloadTheApp.completed',
  [ActionItemTypes.DOWNLOAD_THE_INVOICES_APP]: 'setupGuide.actionItems.downloadTheInvoicesApp.completed',
  [ActionItemTypes.DOWNLOAD_THE_RETAIL_APP]: 'setupGuide.actionItems.downloadTheRetailApp.completed',
  [ActionItemTypes.DOWNLOAD_THE_RST_APP]: 'setupGuide.actionItems.downloadTheRestaurantsApp.completed',
  [ActionItemTypes.ENTER_DIRECTOR_INFO]: 'setupGuide.actionItems.enterDirectorInfo.completed',
  [ActionItemTypes.ENTER_UK_DIRECTOR_INFO]: 'setupGuide.actionItems.enterUkDirectorInfo.completed',
  [ActionItemTypes.FINISH_ACTIVATING]: {
    [MilestonesVariantTypes.DEFAULT]: 'setupGuide.actionItems.finishActivating.completed.pos',
    [MilestonesVariantTypes.RST]: 'setupGuide.actionItems.finishActivating.completed.restaurants',
    [MilestonesVariantTypes.RST_FREE]: 'setupGuide.actionItems.finishActivating.completed.restaurants'
  },
  [ActionItemTypes.SUBMIT_DOC_IDV]: 'setupGuide.actionItems.finishActivating.completed.pos',
  [ActionItemTypes.FINISH_OWNER_DIRECTOR_IDV]: 'setupGuide.actionItems.finishActivating.completed.pos',
  [ActionItemTypes.LINK_YOUR_BANK_ACCOUNT]: 'setupGuide.actionItems.linkYourBankAccount.completed',
  [ActionItemTypes.MANAGE_EMPLOYEES]: {
    [MilestonesVariantTypes.DEFAULT]: 'setupGuide.actionItems.manageEmployees.completed.pos',
    [MilestonesVariantTypes.RETAIL]: 'setupGuide.actionItems.manageEmployees.completed.retail',
    [MilestonesVariantTypes.RST]: 'setupGuide.actionItems.manageEmployees.completed.restaurants',
    [MilestonesVariantTypes.RST_FREE]: 'setupGuide.actionItems.manageEmployees.completed.restaurants'
  },
  [ActionItemTypes.SAVED_A_MENU_ITEM]: {
    [MilestonesVariantTypes.DEFAULT]: 'setupGuide.actionItems.savedAMenuItem.completed.pos',
    [MilestonesVariantTypes.RST]: 'setupGuide.actionItems.savedAMenuItem.completed.restaurants',
    [MilestonesVariantTypes.RST_FREE]: 'setupGuide.actionItems.savedAMenuItem.completed.restaurants'
  },
  [ActionItemTypes.SELL_WITH_SQUARE_FOR_RETAIL]: 'setupGuide.actionItems.sellWithSquareForRetail.completed',
  [ActionItemTypes.SEND_AN_INVOICE]: 'setupGuide.actionItems.sendAnInvoice.completed',
  [ActionItemTypes.SET_CLOSE_OF_DAY]: 'setupGuide.actionItems.setCloseOfDay.completed',
  [ActionItemTypes.SET_UP_DEVICES]: {
    [MilestonesVariantTypes.DEFAULT]: 'setupGuide.actionItems.setUpDevices.completed.pos',
    [MilestonesVariantTypes.RST]: 'setupGuide.actionItems.setUpDevices.completed.restaurants',
    [MilestonesVariantTypes.RST_FREE]: 'setupGuide.actionItems.setUpDevices.completed.restaurants'
  },
  [ActionItemTypes.SET_UP_DISCOUNTS]: 'setupGuide.actionItems.setUpDiscounts.completed',
  [ActionItemTypes.SET_UP_FLOOR_PLAN]: {
    [MilestonesVariantTypes.DEFAULT]: 'setupGuide.actionItems.setUpFloorPlan.completed.pos',
    [MilestonesVariantTypes.RST]: 'setupGuide.actionItems.setUpFloorPlan.completed.restaurants',
    [MilestonesVariantTypes.RST_FREE]: 'setupGuide.actionItems.setUpFloorPlan.completed.restaurants'
  },
  [ActionItemTypes.SET_UP_ITEMS]: {
    [MilestonesVariantTypes.DEFAULT]: 'setupGuide.actionItems.setUpItems.completed.pos',
    [MilestonesVariantTypes.RETAIL]: 'setupGuide.actionItems.setUpItems.completed.retail'
  },
  [ActionItemTypes.SET_UP_RETAIL_DEVICES]: 'setupGuide.actionItems.setUpRetailDevices.completed',
  // TODO(RST-5601) Delete SET_UP_RST_GRID_LAYOUT once removed from Checklist
  [ActionItemTypes.SET_UP_RST_GRID_LAYOUT]: 'setupGuide.actionItems.setUpRstGridLayout.completed',
  [ActionItemTypes.SET_UP_TAXES]: {
    [MilestonesVariantTypes.DEFAULT]: 'setupGuide.actionItems.setUpTaxes.completed.pos',
    [MilestonesVariantTypes.RST]: 'setupGuide.actionItems.setUpTaxes.completed.restaurants',
    [MilestonesVariantTypes.RST_FREE]: 'setupGuide.actionItems.setUpTaxes.completed.restaurants'
  },
  [ActionItemTypes.SET_UP_VENDORS]: 'setupGuide.actionItems.setUpVendors.completed',
  [ActionItemTypes.SET_UP_ECOMMERCE_STORE]: 'setupGuide.actionItems.ecommercePlus.completed',
  [ActionItemTypes.SUBSCRIBE_TO_RETAIL]: 'setupGuide.actionItems.subscribeToRetail.completed',
  [ActionItemTypes.TAKE_FIRST_PAYMENT]: 'setupGuide.actionItems.takeFirstPayment.completed',
  [ActionItemTypes.UPDATE_BUSINESS_DETAILS]: 'setupGuide.actionItems.updateBusinessDetails.completed',
  [ActionItemTypes.VIEW_INVOICE_SETTINGS]: 'setupGuide.actionItems.viewInvoiceSettings.completed',
  [ActionItemTypes.VIEW_SALES_REPORTS]: 'setupGuide.actionItems.viewSalesReports.completed',
  [ActionItemTypes.VIEW_SOFTWARE_OVERVIEW]: 'setupGuide.actionItems.viewSoftwareOverview.completed',
  [ActionItemTypes.ENABLE_PICKUP_AND_DELIVERY]: 'setupGuide.actionItems.enablePickupAndDelivery.completed',
  [ActionItemTypes.SET_UP_KDS]: 'setupGuide.actionItems.setUpKDS.completed',
  [ActionItemTypes.SIGN_UP_APPOINTMENTS]: 'setupGuide.actionItems.signUpAppointments.completed',
  [ActionItemTypes.ADD_BILLING_INSTRUMENT]: 'setupGuide.title',
  [ActionItemTypes.PURCHASE_PRICING_PLAN_HARDWARE]: 'setupGuide.title'
};

// stepCompleteTranslationKeys and buttonNextTranslationKeys have default keys defined in their translate methods
// so don't need to set a default in the config here.
const stepCompleteTranslationKeys = {
  [ActionItemTypes.CUSTOMIZE_RECEIPTS]: {
    [MilestonesVariantTypes.RST]: 'setupGuide.actionItems.customizeReceipts.stepComplete.restaurants',
    [MilestonesVariantTypes.RST_FREE]: 'setupGuide.actionItems.customizeReceipts.stepComplete.restaurants'
  },
  [ActionItemTypes.DOWNLOAD_THE_RST_APP]: {
    [MilestonesVariantTypes.RST]: 'setupGuide.actionItems.downloadTheRestaurantsApp.stepComplete',
    [MilestonesVariantTypes.RST_FREE]: 'setupGuide.actionItems.downloadTheRestaurantsApp.stepComplete'
  },
  [ActionItemTypes.MANAGE_EMPLOYEES]: {
    [MilestonesVariantTypes.RST]: 'setupGuide.actionItems.manageEmployees.stepComplete.restaurants',
    [MilestonesVariantTypes.RST_FREE]: 'setupGuide.actionItems.manageEmployees.stepComplete.restaurants'
  },
  [ActionItemTypes.SAVED_A_MENU_ITEM]: {
    [MilestonesVariantTypes.RST]: 'setupGuide.actionItems.savedAMenuItem.stepComplete',
    [MilestonesVariantTypes.RST_FREE]: 'setupGuide.actionItems.savedAMenuItem.stepComplete'
  },
  [ActionItemTypes.SET_UP_DEVICES]: {
    [MilestonesVariantTypes.RST]: 'setupGuide.actionItems.setUpDevices.stepComplete.restaurants',
    [MilestonesVariantTypes.RST_FREE]: 'setupGuide.actionItems.setUpDevices.stepComplete.restaurants'
  },
  [ActionItemTypes.SET_UP_FLOOR_PLAN]: {
    [MilestonesVariantTypes.RST]: 'setupGuide.actionItems.setUpFloorPlan.stepComplete',
    [MilestonesVariantTypes.RST_FREE]: 'setupGuide.actionItems.setUpFloorPlan.stepComplete'
  },
  [ActionItemTypes.SET_UP_TAXES]: {
    [MilestonesVariantTypes.RST]: 'setupGuide.actionItems.setUpTaxes.stepComplete.restaurants',
    [MilestonesVariantTypes.RST_FREE]: 'setupGuide.actionItems.setUpTaxes.stepComplete.restaurants'
  }
};
const buttonNextTranslationKeys = {
  [ActionItemTypes.BUY_HARDWARE]: {
    [MilestonesVariantTypes.RST]: 'setupGuide.actionItems.buyHardware.buttonNext',
    [MilestonesVariantTypes.RST_FREE]: 'setupGuide.actionItems.buyHardware.buttonNext'
  },
  [ActionItemTypes.CUSTOMIZE_RECEIPTS]: {
    [MilestonesVariantTypes.RST]: 'setupGuide.actionItems.customizeReceipts.buttonNext',
    [MilestonesVariantTypes.RST_FREE]: 'setupGuide.actionItems.customizeReceipts.buttonNext'
  },
  [ActionItemTypes.DOWNLOAD_THE_RST_APP]: {
    [MilestonesVariantTypes.RST]: 'setupGuide.actionItems.downloadTheRestaurantsApp.buttonNext',
    [MilestonesVariantTypes.RST_FREE]: 'setupGuide.actionItems.downloadTheRestaurantsApp.buttonNext'
  },
  [ActionItemTypes.FINISH_ACTIVATING]: {
    [MilestonesVariantTypes.RST]: 'setupGuide.actionItems.finishActivating.buttonNext',
    [MilestonesVariantTypes.RST_FREE]: 'setupGuide.actionItems.finishActivating.buttonNext'
  },
  [ActionItemTypes.SUBMIT_DOC_IDV]: 'setupGuide.actionItems.finishActivating.buttonNext',
  [ActionItemTypes.FINISH_OWNER_DIRECTOR_IDV]: 'setupGuide.actionItems.finishOwnerDirectorIdv.buttonNext',
  [ActionItemTypes.MANAGE_EMPLOYEES]: {
    [MilestonesVariantTypes.RST]: 'setupGuide.actionItems.manageEmployees.buttonNext',
    [MilestonesVariantTypes.RST_FREE]: 'setupGuide.actionItems.manageEmployees.buttonNext'
  },
  [ActionItemTypes.SAVED_A_MENU_ITEM]: {
    [MilestonesVariantTypes.RST]: 'setupGuide.actionItems.savedAMenuItem.buttonNext',
    [MilestonesVariantTypes.RST_FREE]: 'setupGuide.actionItems.savedAMenuItem.buttonNext'
  },
  [ActionItemTypes.SET_UP_DEVICES]: {
    [MilestonesVariantTypes.RST]: 'setupGuide.actionItems.setUpDevices.buttonNext',
    [MilestonesVariantTypes.RST_FREE]: 'setupGuide.actionItems.setUpDevices.buttonNext'
  },
  [ActionItemTypes.SET_UP_FLOOR_PLAN]: {
    [MilestonesVariantTypes.RST]: 'setupGuide.actionItems.setUpFloorPlan.buttonNext',
    [MilestonesVariantTypes.RST_FREE]: 'setupGuide.actionItems.setUpFloorPlan.buttonNext'
  },
  [ActionItemTypes.SET_UP_TAXES]: {
    [MilestonesVariantTypes.RST]: 'setupGuide.actionItems.setUpTaxes.buttonNext',
    [MilestonesVariantTypes.RST_FREE]: 'setupGuide.actionItems.setUpTaxes.buttonNext'
  }
};
const buttonTranslationKeys = {
  [ActionItemTypes.BUY_HARDWARE]: {
    [MilestonesVariantTypes.DEFAULT]: 'setupGuide.actionItems.buyHardware.button.pos',
    [MilestonesVariantTypes.RETAIL]: 'setupGuide.actionItems.buyHardware.button.retail'
  },
  [ActionItemTypes.BUY_POS_HARDWARE]: 'setupGuide.actionItems.buyPosHardware.button',
  [ActionItemTypes.CREATE_BAR_CODE_LABEL]: 'setupGuide.actionItems.createBarCodeLabel.button',
  [ActionItemTypes.CUSTOMIZE_RECEIPTS]: {
    [MilestonesVariantTypes.DEFAULT]: 'setupGuide.actionItems.customizeReceipts.button.pos',
    [MilestonesVariantTypes.RETAIL]: 'setupGuide.actionItems.customizeReceipts.button.retail'
  },
  [ActionItemTypes.DOWNLOAD_THE_APP]: 'setupGuide.actionItems.downloadTheApp.button',
  [ActionItemTypes.DOWNLOAD_THE_INVOICES_APP]: 'setupGuide.actionItems.downloadTheInvoicesApp.button',
  [ActionItemTypes.DOWNLOAD_THE_RETAIL_APP]: 'setupGuide.actionItems.downloadTheRetailApp.button',
  [ActionItemTypes.DOWNLOAD_THE_RST_APP]: 'setupGuide.actionItems.downloadTheRestaurantsApp.button',
  [ActionItemTypes.ENABLE_PICKUP_AND_DELIVERY]: 'setupGuide.actionItems.enablePickupAndDelivery.button',
  [ActionItemTypes.ENTER_DIRECTOR_INFO]: 'setupGuide.actionItems.enterDirectorInfo.button',
  [ActionItemTypes.ENTER_UK_DIRECTOR_INFO]: 'setupGuide.actionItems.enterUkDirectorInfo.button',
  [ActionItemTypes.FINISH_ACTIVATING]: {
    [MilestonesVariantTypes.DEFAULT]: 'setupGuide.actionItems.finishActivating.button.pos',
    [MilestonesVariantTypes.RETAIL]: 'setupGuide.actionItems.finishActivating.button.retail'
  },
  [ActionItemTypes.SUBMIT_DOC_IDV]: 'setupGuide.actionItems.finishActivating.button.pos',
  [ActionItemTypes.FINISH_OWNER_DIRECTOR_IDV]: 'setupGuide.actionItems.finishOwnerDirectorIdv.buttonNext',
  [ActionItemTypes.IMPORT_INVENTORY]: 'setupGuide.actionItems.importInventory.button',
  [ActionItemTypes.LINK_YOUR_BANK_ACCOUNT]: {
    [MilestonesVariantTypes.DEFAULT]: 'setupGuide.actionItems.linkYourBankAccount.button.pos',
    [MilestonesVariantTypes.RETAIL]: 'setupGuide.actionItems.linkYourBankAccount.button.retail'
  },
  [ActionItemTypes.MANAGE_EMPLOYEES]: {
    [MilestonesVariantTypes.DEFAULT]: 'setupGuide.actionItems.manageEmployees.button.pos',
    [MilestonesVariantTypes.RETAIL]: 'setupGuide.actionItems.manageEmployees.button.retail'
  },
  [ActionItemTypes.SAVED_A_MENU_ITEM]: 'setupGuide.actionItems.savedAMenuItem.button',
  [ActionItemTypes.SET_UP_DEVICES]: 'setupGuide.actionItems.setUpDevices.button',
  [ActionItemTypes.SET_UP_FLOOR_PLAN]: 'setupGuide.actionItems.setUpFloorPlan.button',
  [ActionItemTypes.SET_UP_TAXES]: {
    [MilestonesVariantTypes.DEFAULT]: 'setupGuide.actionItems.setUpTaxes.button.pos',
    [MilestonesVariantTypes.RETAIL]: 'setupGuide.actionItems.setUpTaxes.button.retail'
  },
  [ActionItemTypes.SUBSCRIBE_TO_RETAIL]: 'setupGuide.actionItems.subscribeToRetail.button',
  [ActionItemTypes.VERIFY_EMAIL]: 'setupGuide.actionItems.verifyEmail.button',
  [ActionItemTypes.VIEW_SOFTWARE_OVERVIEW]: 'setupGuide.actionItems.viewSoftwareOverview.title',
  [ActionItemTypes.SET_UP_ONLINE_CHECKOUT]: {
    [MilestonesVariantTypes.DEFAULT]: 'setupGuide.actionItems.setupOnlineCheckout.primary.button',
    [MilestonesVariantTypes.ONLINE_CHECKOUT]: 'setupGuide.actionItems.setupOnlineCheckout.primary.button'
  }
};
const imgAltTranslationKeys = {
  [ActionItemTypes.BUY_HARDWARE]: 'setupGuide.actionItems.buyHardware.imgAlt',
  [ActionItemTypes.CUSTOMIZE_RECEIPTS]: 'setupGuide.actionItems.customizeReceipts.imgAlt',
  [ActionItemTypes.FINISH_ACTIVATING]: 'setupGuide.actionItems.finishActivating.imgAlt',
  [ActionItemTypes.SUBMIT_DOC_IDV]: 'setupGuide.actionItems.finishActivating.imgAlt',
  [ActionItemTypes.FINISH_OWNER_DIRECTOR_IDV]: 'setupGuide.actionItems.finishActivating.imgAlt',
  [ActionItemTypes.LINK_YOUR_BANK_ACCOUNT]: 'setupGuide.actionItems.linkYourBankAccount.imgAlt',
  [ActionItemTypes.SET_CLOSE_OF_DAY]: 'setupGuide.actionItems.setCloseOfDay.imgAlt',
  [ActionItemTypes.SET_UP_KDS]: 'setupGuide.actionItems.setUpKDS.imgAlt',
  [ActionItemTypes.DOWNLOAD_THE_KDS_APP]: 'setupGuide.actionItems.downloadTheKDSApp.imgAlt',
  [ActionItemTypes.VIEW_KITCHEN_REPORTS]: 'setupGuide.actionItems.viewKitchenReports.imgAlt',
  [ActionItemTypes.SET_UP_TAXES]: 'setupGuide.actionItems.setUpTaxes.imgAlt'
};
const secondaryButtonTranslationKeys = {
  [ActionItemTypes.CONNECT_WEEBLY]: 'setupGuide.actionItems.connectWeebly.secondaryButton',
  [ActionItemTypes.FINISH_OWNER_DIRECTOR_IDV]: 'setupGuide.actionItems.finishOwnerDirectorIdv.secondaryButton',
  [ActionItemTypes.SET_UP_ECOMMERCE_STORE]: 'setupGuide.actionItems.ecommercePlus.secondaryButton',
  [ActionItemTypes.SET_UP_BUTTON_SETTINGS]: 'setupGuide.actionItems.setupButtonSettings.secondaryButton',
  [ActionItemTypes.SET_UP_FULFILLMENT_SETTINGS]: 'setupGuide.actionItems.setupFulfillmentSettings.secondaryButton'
};
const videoAltTranslationKeys = {
  [ActionItemTypes.DOWNLOAD_THE_RST_APP]: 'setupGuide.actionItems.downloadTheRestaurantsApp.videoAlt',
  [ActionItemTypes.IMPORT_INVENTORY]: 'setupGuide.actionItems.importInventory.videoAlt',
  [ActionItemTypes.MANAGE_EMPLOYEES]: 'setupGuide.actionItems.manageEmployees.videoAlt',
  [ActionItemTypes.SAVED_A_MENU_ITEM]: 'setupGuide.actionItems.savedAMenuItem.videoAlt',
  [ActionItemTypes.SET_UP_DEVICES]: 'setupGuide.actionItems.setUpDevices.videoAlt',
  [ActionItemTypes.SET_UP_FLOOR_PLAN]: 'setupGuide.actionItems.setUpFloorPlan.videoAlt',
  [ActionItemTypes.SET_UP_ITEMS]: 'setupGuide.actionItems.setUpItems.videoAlt',
  [ActionItemTypes.SET_UP_RETAIL_DEVICES]: 'setupGuide.actionItems.setUpRetailDevices.videoAlt'
};
const videoTitleTranslationKeys = {
  [ActionItemTypes.DOWNLOAD_THE_RST_APP]: 'setupGuide.actionItems.downloadTheRestaurantsApp.videoTitle',
  [ActionItemTypes.IMPORT_INVENTORY]: 'setupGuide.actionItems.importInventory.videoTitle',
  [ActionItemTypes.MANAGE_EMPLOYEES]: 'setupGuide.actionItems.manageEmployees.videoTitle',
  [ActionItemTypes.SAVED_A_MENU_ITEM]: 'setupGuide.actionItems.savedAMenuItem.videoTitle',
  [ActionItemTypes.SET_UP_DEVICES]: 'setupGuide.actionItems.setUpDevices.videoTitle',
  [ActionItemTypes.SET_UP_FLOOR_PLAN]: 'setupGuide.actionItems.setUpFloorPlan.videoTitle',
  [ActionItemTypes.SET_UP_ITEMS]: 'setupGuide.actionItems.setUpItems.videoTitle',
  [ActionItemTypes.SET_UP_RETAIL_DEVICES]: 'setupGuide.actionItems.setUpRetailDevices.videoTitle'
};
const videoDescriptionTranslationKeys = {
  [ActionItemTypes.DOWNLOAD_THE_RST_APP]: 'setupGuide.actionItems.downloadTheRestaurantsApp.videoDescription',
  [ActionItemTypes.IMPORT_INVENTORY]: 'setupGuide.actionItems.importInventory.videoDescription',
  [ActionItemTypes.MANAGE_EMPLOYEES]: 'setupGuide.actionItems.manageEmployees.videoDescription',
  [ActionItemTypes.SAVED_A_MENU_ITEM]: 'setupGuide.actionItems.savedAMenuItem.videoDescription',
  [ActionItemTypes.SET_UP_DEVICES]: 'setupGuide.actionItems.setUpDevices.videoDescription',
  [ActionItemTypes.SET_UP_FLOOR_PLAN]: 'setupGuide.actionItems.setUpFloorPlan.videoDescription',
  [ActionItemTypes.SET_UP_ITEMS]: 'setupGuide.actionItems.setUpItems.videoDescription',
  [ActionItemTypes.SET_UP_RETAIL_DEVICES]: 'setupGuide.actionItems.setUpRetailDevices.videoDescription'
};

// eslint-disable-next-line ember/no-classic-classes
export default Service.extend({
  i18n: service(),
  setupGuide: service(),
  milestonesVariant: readOnly('setupGuide.milestonesVariant'),
  _getTranslation(translationKeys, actionItemName, {
    defaultKey,
    translationParams
  } = {}) {
    const translationKey = translationKeys[actionItemName];
    if (typeOf(translationKey) === 'string') {
      return this.i18n.t(translationKey, translationParams);
    } else if (typeOf(translationKey) === 'object') {
      if (this.milestonesVariant in translationKey) {
        return this.i18n.t(translationKey[this.milestonesVariant], translationParams);
      } else if (MilestonesVariantTypes.DEFAULT in translationKey) {
        return this.i18n.t(translationKey[MilestonesVariantTypes.DEFAULT], translationParams);
      }
    }
    if (defaultKey) {
      return this.i18n.t(defaultKey);
    } else {
      assert(`Translation for Action Item ${actionItemName} does not exist`);
      return null;
    }
  },
  getTitle(actionItemName) {
    return this._getTranslation(titleTranslationKeys, actionItemName);
  },
  getModalTitle(actionItemName) {
    if (modalTitleTranslationKeys[actionItemName]) {
      return this._getTranslation(modalTitleTranslationKeys, actionItemName);
    }
    return this.getTitle(actionItemName);
  },
  getIntroduction(actionItemName) {
    return this._getTranslation(introductionTranslationKeys, actionItemName);
  },
  getDescription(actionItemName) {
    return this._getTranslation(descriptionTranslationKeys, actionItemName);
  },
  getCompleted(actionItemName) {
    return this._getTranslation(completedTranslationKeys, actionItemName);
  },
  getButton(actionItemName) {
    return this._getTranslation(buttonTranslationKeys, actionItemName, {
      defaultKey: DEFAULT_BUTTON_TRANSLATION_KEY
    });
  },
  getStepComplete(actionItemName, translationParams) {
    return this._getTranslation(stepCompleteTranslationKeys, actionItemName, {
      defaultKey: DEFAULT_STEP_COMPLETE_TRANSLATION_KEY,
      translationParams
    });
  },
  getButtonNext(actionItemName) {
    return this._getTranslation(buttonNextTranslationKeys, actionItemName, {
      defaultKey: DEFAULT_BUTTON_NEXT_TRANSLATION_KEY
    });
  },
  getImgAlt(actionItemName) {
    return this._getTranslation(imgAltTranslationKeys, actionItemName);
  },
  getSecondaryButton(actionItemName) {
    const defaultKey = DEFAULT_SECONDARY_BUTTON_TRANSLATION_KEYS[this.setupGuide.milestonesVariant] || DEFAULT_SECONDARY_BUTTON_TRANSLATION_KEYS[MilestonesVariantTypes.DEFAULT];
    return this._getTranslation(secondaryButtonTranslationKeys, actionItemName, {
      defaultKey
    });
  },
  getVideoAlt(actionItemName) {
    return this._getTranslation(videoAltTranslationKeys, actionItemName);
  },
  getVideoTitle(actionItemName) {
    return this._getTranslation(videoTitleTranslationKeys, actionItemName);
  },
  getVideoDescription(actionItemName) {
    return this._getTranslation(videoDescriptionTranslationKeys, actionItemName);
  }
});